import { apiClient } from "@/lib/apiClient"

/**
 * Backend auth endpoint'leri (euas-backend ile uyumlu)
 * Base: /api/auth
 *
 * Pentest 3.3.3: OTP ile giriş, telefon doğrulama ve e-posta ile şifre sıfırlama
 * uçları backend'de feature flag arkasına alındı (varsayılan kapalı → 404).
 * FE'de bu akışlara ait ölü kod (çağrılar + ekranlar) kaldırıldı.
 */
const ENDPOINTS = {
    /** Yönetici ve çalışan için aynı LDAP login endpoint */
    LOGIN_LDAP: "/api/auth/login/ldap",
    /** ALTCHA proof-of-work challenge (public, throttled) */
    CAPTCHA_CHALLENGE: "/api/auth/captcha/challenge",
    REFRESH: "/api/auth/refresh",
    LOGOUT: "/api/auth/logout",
    DOMAIN: "/api/domain/public",
    SELECT_COMPANY: "/api/staff-portal/switch",
} as const

/**
 * Girişin yapıldığı sekme. Backend `ADMIN` portalında yönetici olmayan
 * (kursiyer/stajyer) hesaplara token vermez ve yanlış şifreyle birebir aynı
 * 401 LOGIN_FAILED yanıtını döner. Gönderilmezse backend `CLIENT` varsayar.
 */
export type LoginPortal = "ADMIN" | "CLIENT"

export interface LoginRequest {
    username: string
    password: string
    portal: LoginPortal
    /** ALTCHA widget'ının ürettiği base64 payload (tek kullanımlık) */
    captcha?: string
}

/**
 * ALTCHA widget'ının challenge çekeceği mutlak URL.
 * Widget kendi fetch'ini yaptığı için apiClient'ın baseURL'i ile birleştirilir.
 */
export const getCaptchaChallengeUrl = (): string => {
    const base = String(apiClient.defaults.baseURL || "").replace(/\/+$/, "")
    return `${base}${ENDPOINTS.CAPTCHA_CHALLENGE}`
}

export const authApi = {
    /**
     * LDAP Login - Hem yönetici hem çalışan için aynı endpoint
     * POST /api/auth/login/ldap { username, password, portal, captcha? }
     */
    login: async (data: LoginRequest) => {
        // skipErrorToast: interceptor'ın genel toast'ını sustur — login bileşenleri
        // hatayı kendileri (genel "kullanıcı adı veya şifre hatalı" mesajıyla)
        // gösteriyor; backend mesajının ham hâliyle ekrana düşmesini de engeller.
        const response = await apiClient.post(
            ENDPOINTS.LOGIN_LDAP,
            {
                username: data.username,
                password: data.password,
                portal: data.portal,
                ...(data.captcha ? { captcha: data.captcha } : {}),
            },
            { skipErrorToast: true },
        )
        return response.data
    },

    /**
     * Token yenile
     */
    refresh: async (refreshToken: string) => {
        const response = await apiClient.post(ENDPOINTS.REFRESH, { refreshToken })
        return response.data
    },

    /**
     * Çıkış
     */
    logout: async (
        refreshToken: string,
        options?: { skipErrorToast?: boolean; accessToken?: string },
    ) => {
        // accessToken verilirse açıkça gönderilir: çağıran token'ları cookie'den
        // hemen silse bile (request interceptor yalnızca cookie'de token varsa
        // Authorization'ı ezer) istek doğru oturumla gider.
        const response = await apiClient.post(
            ENDPOINTS.LOGOUT,
            { refreshToken },
            {
                skipErrorToast: options?.skipErrorToast,
                ...(options?.accessToken
                    ? { headers: { Authorization: `Bearer ${options.accessToken}` } }
                    : {}),
            },
        )
        return response.data
    },

    /**
     * Domain bilgisi (public)
     */
    getDomain: async (domainName: string) => {
        const response = await apiClient.get(`${ENDPOINTS.DOMAIN}/${domainName}`)
        return response.data
    },

    /**
     * Şirket/domain seç (staff portal)
     */
    selectCompany: async (company: { key: string }) => {
        const params = new URLSearchParams({ domain: company.key })
        const response = await apiClient.get(`${ENDPOINTS.SELECT_COMPANY}?${params}`)
        return response.data
    },
}
